import Link from "next/link";
import { useRouter } from "next/router";
import * as React from "react";
import {
  TwitterShareButton,
  FacebookShareButton,
  LineShareButton,
} from "react-share";
import { Button } from "@components/Button";
import { PoweredByArea } from "@components/PoweredByArea";
import type { ShopDetailLayout_ShopFragment } from "@components/ShopDetail/Layout/__generated__/fragments";
import { useShopDetailLayout_PersonalShopDataQuery } from "@components/ShopDetail/Layout/__generated__/queries";
import Schedule from "@components/ShopDetail/Schedule";
import TogglableIconButton from "@components/TogglableIconButton";
import Auth from "@hooks/useAuth";
import { useShare } from "@hooks/useShare";
import useToggleFavoriteShop from "@hooks/useToggleFavoriteShop";
import { paths } from "@libs/paths";
import { GTMEvent, GTMEvents } from "@libs/utils/gtm";
import Footer from "./Footer";
import Tab from "./Tab";
import styles from "./index.module.css";
import type { FC, ReactNode } from "react";

const RESERVE_BUTTON_LABEL = "ネット予約する";
const UNAUTHENTICATED_RESERVE_BUTTON_PREFIX = "登録して";

type Props = {
  shop: ShopDetailLayout_ShopFragment;
  current?: "top" | "shopMatch" | "shopPhoto";
  showsPoweredByArea?: boolean;
  children: ReactNode;
};

const Layout: FC<Props> = ({
  shop,
  children,
  current = "top",
  showsPoweredByArea = true,
}) => {
  const router = useRouter();
  const {
    isCurrentUserReady,
    isFanstaUserReady,
    isFanstaUserRegisteredAndValidated,
  } = Auth.useContainer();
  const showsUnauthenticatedReserveLabel =
    isCurrentUserReady &&
    isFanstaUserReady &&
    !isFanstaUserRegisteredAndValidated;

  const shopUuid = shop.uuid;
  const { data } = useShopDetailLayout_PersonalShopDataQuery({
    variables: {
      shopUuids: [shopUuid],
    },
    skip: !isFanstaUserRegisteredAndValidated,
  });
  const initialIsFavorite =
    data?.personalShopData?.find((d) => d.uuid === shop?.uuid)?.isFavorite ??
    false;
  const { toggle, isFavorite } = useToggleFavoriteShop(
    shopUuid,
    initialIsFavorite
  );

  const photoPath = "/photo";
  const matchPath = "/matches";

  const shopName = shop.name;
  const shopDetailUrl =
    process.env.APP_HOST + paths.shopDetail(shop.uuid || "");
  const shareText =
    shopName +
    "\n" +
    shop.telWithHyphen +
    "\n" +
    shop.masterPrefecture.name +
    shop.city +
    shop.block +
    (shop.building ? "\n" + shop.building : "") +
    "\n" +
    shopDetailUrl;

  const {
    copy,
    showToast,
    shareButtonsElementRef,
    isShareOpen,
    toggleShareOpen,
  } = useShare(shareText);

  const handleToReservationClick = () => {
    GTMEvent(
      GTMEvents.toReservationFormPressed("ネット予約する", shopUuid || "")
    );
  };

  return (
    <>
      <div>
        {shopUuid && <Tab shopUuid={shopUuid} current={current} />}

        <div className={styles.column}>
          <div className={styles.columnLeft}>{children}</div>

          <div className={styles.columnRight}>
            {!router.pathname.includes(photoPath) &&
              !router.pathname.includes(matchPath) && (
                <div className="md:none">
                  <Footer
                    tel={shop.tel}
                    telWithHyphen={shop.telWithHyphen}
                    name={shop.name}
                    shopUuid={shop.uuid || ""}
                    canReserve={shop.isReservableOnline}
                    initialIsFavorite={initialIsFavorite}
                    showsUnauthenticatedReserveLabel={
                      showsUnauthenticatedReserveLabel
                    }
                  />
                </div>
              )}

            <div className={styles.sticky}>
              <div className="none md:block">
                <div className={styles.pcFooterWrapper}>
                  <div className={styles.pcFooter}>
                    <p className={styles.pcTel}>
                      <span>{shop.telWithHyphen}</span>
                    </p>
                  </div>
                  <div className={styles.pcFooterRepletion}>
                    お電話の際は「Fanstaを見た」とお伝えいただくとスムーズです。
                  </div>
                </div>

                {shop.isReservableOnline && (
                  <div className={styles.reservation}>
                    <Link
                      href={paths.shopRequestReservation(shopUuid || "")}
                      legacyBehavior
                    >
                      <Button
                        className={styles.btn}
                        onClick={handleToReservationClick}
                        styleType="filled"
                        colorType="primary"
                      >
                        <span>
                          {showsUnauthenticatedReserveLabel ? (
                            <>
                              {UNAUTHENTICATED_RESERVE_BUTTON_PREFIX}
                              <wbr />
                              {RESERVE_BUTTON_LABEL}
                            </>
                          ) : (
                            RESERVE_BUTTON_LABEL
                          )}
                        </span>
                      </Button>
                    </Link>
                  </div>
                )}
                <div className={styles.actionButtons}>
                  <div className={styles.pcBookmark}>
                    <TogglableIconButton
                      inactiveIconPath="/img/icon_bookmark_outline.svg"
                      activeIconPath="/img/icon_bookmark_fill.svg"
                      label={"お気に入り"}
                      isActive={isFavorite}
                      onClick={toggle}
                      layout="small"
                    />
                  </div>

                  <div className={styles.pcShare}>
                    <TogglableIconButton
                      inactiveIconPath="/img/icon_share_white.svg"
                      activeIconPath="/img/icon_share_yellow.svg"
                      label={"シェア"}
                      isActive={isShareOpen}
                      onClick={(ev: React.MouseEvent): void => {
                        ev.stopPropagation();
                        !isShareOpen &&
                          GTMEvent(GTMEvents.shareButtonPressed("preview"));
                        toggleShareOpen();
                      }}
                      layout="small"
                    />
                    <div
                      className={`${styles.shareBox} ${
                        isShareOpen ? styles.active : ""
                      }`}
                    >
                      <div className={styles.box} ref={shareButtonsElementRef}>
                        <p className={styles.boxText}>
                          {shop.name}
                          <br />
                          {shop.telWithHyphen}
                          <br />
                          {shop.masterPrefecture.name}
                          {shop.city}
                          {shop.block}
                          {shop.building && (
                            <>
                              <br />
                              {shop.building}
                            </>
                          )}
                          <br />
                          {shopDetailUrl}
                        </p>
                        <div className={styles.boxBtn}>
                          <TwitterShareButton
                            className={styles.tweetBtn}
                            url={shopDetailUrl}
                            title={shopName}
                          >
                            <img
                              src="/img/button_x.svg"
                              width="44"
                              height="44"
                              alt="X"
                            />
                          </TwitterShareButton>
                          <FacebookShareButton
                            className={styles.shareBtn}
                            url={shopDetailUrl}
                          >
                            <img
                              src="/img/button_facebook.svg"
                              width="44"
                              height="44"
                              alt="Facebook"
                            />
                          </FacebookShareButton>
                          <LineShareButton
                            className={styles.lineBtn}
                            url={shopDetailUrl}
                            title={shopName}
                          >
                            <img
                              src="/img/button_line.svg"
                              width="44"
                              height="44"
                              alt="Line"
                            />
                          </LineShareButton>
                          <button
                            className={styles.copyBtn}
                            onClick={() => {
                              GTMEvent(GTMEvents.shareButtonPressed("copy"));
                              copy();
                            }}
                          >
                            <img
                              src="/img/button_copy.svg"
                              width="44"
                              height="44"
                              alt="Copy"
                            />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <Schedule shop={shop} isColumn />
              </div>
            </div>

            <div
              className={`${styles.copyText} ${showToast ? styles.active : ""}`}
            >
              <p>店舗情報テキストをコピーしました</p>
            </div>
          </div>
        </div>
      </div>

      {showsPoweredByArea && (
        <div className="md:none">
          <PoweredByArea />
        </div>
      )}
    </>
  );
};

export default Layout;
